import { useMemo, useState, type FormEvent } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { saveEnsemble, setEnsembleMembers } from '../../../Redux/ensemblesSlice';
import { selectActiveTaughtStudents, selectOrganizations } from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { ApiError } from '../../../Models/ApiModel';
import type { EnsembleModel } from '../../../Models/EnsembleModel';
import { EnsembleBilling, billingHasGroupPrice, billingHasPayer } from '../../../Models/enums';
import { displayNameOf, studentDisplayName } from '../../../Utils/DisplayNameUtil';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { EnsembleSlotEditor } from './EnsembleSlotEditor';
import './Ensembles.css';

interface EnsembleEditorProps {
  /** null creates a group; an ensemble edits that one. */
  ensemble: EnsembleModel | null;
  onClose: () => void;
}

const BILLING_LABELS: Record<string, TranslationKey> = {
  [EnsembleBilling.PerStudent]: 'billingPerStudent',
  [EnsembleBilling.Split]: 'billingSplit',
  [EnsembleBilling.SinglePayer]: 'billingSinglePayer',
  [EnsembleBilling.Institution]: 'billingInstitution'
};

const BILLING_ORDER = [
  EnsembleBilling.Institution,
  EnsembleBilling.PerStudent,
  EnsembleBilling.Split,
  EnsembleBilling.SinglePayer
] as const;

/**
 * The group's card.
 *
 * The billing mode is the field everything else hangs off, so the form shows
 * only what that mode actually uses: a group price appears for the modes that
 * divide one, and the payer picker offers members or organizations depending
 * on who is being billed. A field that mode does not use is not disabled, it
 * is absent, because a stored figure nobody reads is how last term's price
 * gets billed by accident.
 *
 * `institution` is listed first. It is how most ensemble teaching is actually
 * paid for: a conservatory or a music programme puts the group together and
 * pays for the whole thing, and the players in the room are taught rather than
 * billed.
 */
export function EnsembleEditor({ ensemble, onClose }: EnsembleEditorProps) {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const students = useAppSelector(selectActiveTaughtStudents);
  const organizations = useAppSelector(selectOrganizations);

  const [name, setName] = useState(ensemble?.name ?? '');
  const [nameEn, setNameEn] = useState(ensemble?.nameEn ?? '');
  const [billingMode, setBillingMode] = useState(ensemble?.billingMode ?? EnsembleBilling.Institution);
  const [price, setPrice] = useState(ensemble?.price ?? '');
  const [payer, setPayer] = useState(ensemble?.payer ?? '');
  const [duration, setDuration] = useState(String(ensemble?.durationMinutes ?? 60));
  const [notes, setNotes] = useState(ensemble?.notes ?? '');
  const [notesEn, setNotesEn] = useState(ensemble?.notesEn ?? '');
  const [archived, setArchived] = useState(Boolean(ensemble?.archived));
  const [members, setMembers] = useState<string[]>(ensemble?.members ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsPrice = billingHasGroupPrice(billingMode);
  const needsPayer = billingHasPayer(billingMode);
  const institution = billingMode === EnsembleBilling.Institution;

  // Who the payer picker offers: an organization bills from outside the
  // group, a single_payer member bills from inside it.
  const payerOptions = useMemo(() => {
    if (!needsPayer) return [];
    if (institution) return organizations.map(organization => organization.name);
    return members;
  }, [needsPayer, institution, organizations, members]);

  function toggleMember(student: string) {
    setMembers(current => current.includes(student)
      ? current.filter(name => name !== student)
      : [...current, student]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const saved = await dispatch(saveEnsemble({
        ...(ensemble ? { name: ensemble.name } : {}),
        draft: {
          name: name.trim(),
          nameEn: nameEn.trim() || null,
          billingMode,
          price: needsPrice ? price.trim() : null,
          // An empty pick is sent as null rather than as an empty string, so
          // the server stores "nobody yet" rather than a name of no length.
          payer: needsPayer ? (payer.trim() || null) : null,
          durationMinutes: Number(duration) || 60,
          notes: notes.trim() || null,
          notesEn: notesEn.trim() || null,
          archived: archived ? 1 : 0
        }
      })).unwrap();
      // Membership has its own endpoint, and is saved after the group exists:
      // a new group has no name to hang members off until it is created.
      await dispatch(setEnsembleMembers({ name: saved.name, members })).unwrap();
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={ensemble ? tr('editEnsembleTitle') : tr('addEnsemble')}
      {...(ensemble ? { subtitle: ensemble.name } : {})}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="submit" form="ensemble-editor" className="primary-button" disabled={busy || !name.trim()}>
            {busy ? <Spinner inline /> : tr('save')}
          </button>
        </>
      }
    >
      <form id="ensemble-editor" className="ensemble-editor" onSubmit={submit}>
        <label className="field">
          <span>{tr('ensembleName')}</span>
          <input className="text-input" value={name} onChange={e => setName(e.target.value)} required />
        </label>
        <label className="field">
          <span>{tr('ensembleNameEn')}</span>
          <input className="text-input" dir="ltr" value={nameEn} onChange={e => setNameEn(e.target.value)} />
        </label>

        <label className="field is-wide">
          <span>{tr('billingLabel')}</span>
          <select
            className="text-input"
            value={billingMode}
            onChange={e => {
              setBillingMode(e.target.value as EnsembleBilling);
              // The payer means something different under each mode, so it is
              // cleared rather than carried across and silently misread.
              setPayer('');
            }}
          >
            {BILLING_ORDER.map(mode => (
              <option key={mode} value={mode}>{tr(BILLING_LABELS[mode] as TranslationKey)}</option>
            ))}
          </select>
        </label>

        {institution && <p className="editor-locked is-wide">{tr('billingInstitutionHint')}</p>}

        {needsPrice && (
          <label className="field">
            <span>{tr('groupPrice')}</span>
            {/* inputMode numeric rather than type=number: the field takes whole
                shekels, and a spinner that offers decimals invites one. */}
            <input
              className="text-input"
              inputMode="numeric"
              value={price}
              onChange={e => setPrice(e.target.value.replace(/\D/g, ''))}
              required
            />
          </label>
        )}

        {needsPayer && (
          <label className="field">
            <span>{institution ? tr('payingOrganization') : tr('payerLabel')}</span>
            <select className="text-input" value={payer} onChange={e => setPayer(e.target.value)}>
              <option value="">{tr('selectPlaceholder')}</option>
              {payerOptions.map(option => (
                <option key={option} value={option}>{displayNameOf(option, institution ? organizations : students, language)}</option>
              ))}
            </select>
          </label>
        )}

        {institution && organizations.length === 0 && (
          <p className="editor-section-error is-wide">{tr('noOrganizations')}</p>
        )}

        <label className="field">
          <span>{tr('ensembleDuration')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={duration}
            onChange={e => setDuration(e.target.value.replace(/\D/g, ''))}
          />
        </label>

        <label className="field is-wide">
          <span>{tr('notes')}</span>
          <textarea className="text-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
        </label>
        <label className="field is-wide">
          <span>{tr('notesEn')}</span>
          <textarea className="text-input" dir="ltr" rows={2} value={notesEn} onChange={e => setNotesEn(e.target.value)} />
        </label>

        <fieldset className="ensemble-member-picker is-wide">
          <legend>{tr('ensembleMembers')}</legend>
          {students.length === 0
            ? <p className="editor-locked">{tr('noStudents')}</p>
            : (
              <ul className="ensemble-member-list">
                {students.map(student => (
                  <li key={student.name}>
                    <label className="check-label">
                      <input
                        type="checkbox"
                        checked={members.includes(student.name)}
                        onChange={() => toggleMember(student.name)}
                      />
                      <span>{studentDisplayName(student, language)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
        </fieldset>

        <label className="check-label is-wide">
          <input type="checkbox" checked={archived} onChange={e => setArchived(e.target.checked)} />
          <span>{tr('archived')}</span>
        </label>

        {error && <p className="student-editor-error" role="alert">{tr('errorGeneric')}</p>}
      </form>

      {/*
        Outside the form, and only for a group that exists. The weekly hour is
        saved against endpoints keyed by the group's name, so there is nothing
        to save it against until the group has one. Keeping it out of the
        <form> also stops its buttons from submitting.
      */}
      {ensemble
        ? <EnsembleSlotEditor ensemble={ensemble} />
        : <p className="editor-locked is-wide">{tr('editorExtrasHint')}</p>}
    </Modal>
  );
}
